type EnumObject<T> = {
  key: T[keyof T];
  label: string;
};

/**
 * Converts an enum object to an array of EnumObject.
 *
 * Example:
 * An enum looks like this:
 * ```typescript
 * enum MyEnum {
 *   FIRST = 1,
 *   SECOND = 2,
 * }
 * ```
 *
 * During runtime, the enum looks like this:
 * ```typescript
 * {
 *  "FIRST": 1,
 *  "SECOND": 2,
 *  "1": "FIRST",
 *  "2": "SECOND",
 * }
 * ```
 *
 * The Return value of this function will look like this:
 * ```typescript
 * [
 *  { key: 1, label: 'FIRST' },
 *  { key: 2, label: 'SECOND' },
 * ]
 * ```
 */
export default <
  T extends {
    [key: string]: number | string;
  },
>(
  val: T,
): EnumObject<T>[] => {
  return (
    Object.keys(val)
      // filter out every non number entries
      .filter((x) => !(parseInt(x, 10) >= 0))
      // filter out the 'UNSPECIFIED' entry
      .filter((x) => x !== 'UNSPECIFIED')
      // map the entries to the EnumObject type
      .map((key) => ({
        key: val[key],
        label: key.toString().replaceAll('_', ' '),
      })) as EnumObject<T>[]
  );
};
